import React, { useEffect, useRef, useState } from 'react';
import {
  createPreview,
  get,
  update,
  remove
} from '../services/session.service';
import {
  add as addSessionPage,
  list as listSessionPages,
  remove as removePage,
  updatePages,
  update as updateSessionPage
} from '../services/sessionPage.service';
import { add as addPage } from '../services/page.service';
import Button from '../components/Button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import NotFound from './NotFound';
import {
  AddSessionRequest,
  SessionDetails,
  SessionPageDetails,
  ShowingDetails
} from '../models/api';
import LoadingSpinner from '../components/LoadingSpinner';
import SessionDetailsForm from '../components/SessionDetailsForm';
import DeleteButton from '../components/DeleteButton';
import { list as listShowings } from '../services/showing.service';
import BackButton from '../components/BackButton';
import { FaGripVertical, FaPlus } from 'react-icons/fa';
import ModalForm from '../components/Modal';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from '@hello-pangea/dnd';
import SessionStyleEditor from '../components/SessionStyleEditor';
import PageEditor from '../components/PageEditor';
import GenericSettingsMenu from '../components/GenericSettingsMenu';
import SessionWelcomeEditor from '../components/SessionWelcomeEditor';
import SessionWelcomePageEditor from '../components/SessionWelcomePageEditor';
import SessionEndEditor from '../components/SessionEndEditor';
import Feedback from '../components/Feedback';
import { ReactComponent as Reply } from '../assets/reply.svg';
import { ReactComponent as PresentIcon } from '../assets/present_to_all_large.svg';
import { ReactComponent as ColoursIcon } from '../assets/colors_medium.svg';
import { ReactComponent as DeleteIcon } from '../assets/delete_large.svg';
import { ReactComponent as EditIcon } from '../assets/edit_large.svg';
import { ReactComponent as RemoveIcon } from '../assets/remove.svg';
import SessionSharingDetails from '../components/SessionSharingDetails';
import EditableLabel from '../components/EditableLabel';
import { useUnsavedChanges } from '../contexts/UnsavedChangesProvider';
import { usePrompt } from '../hooks/usePrompt';
import { useToast } from '../contexts/ToastContext';
import { useRefresh } from '../contexts/RefreshContext';
import { FeatureGate } from '../components/FeatureGate';
import { useAuth } from '../contexts/AuthContext';

const Event: React.FC = () => {
  const [session, setSession] = useState<SessionDetails | null>();
  const [sessionPages, setSessionPages] = useState<SessionPageDetails[]>([]);
  const [loading, setLoading] = useState<boolean | null>();
  const [errors, setErrors] = useState([] as string[]);
  const [selectedPage, setSelectedPage] = useState<SessionPageDetails | null>(
    null
  );
  const { hasFeature } = useAuth();
  enum ShowPageTypeEnum {
    Welcome = 'Welcome',
    About = 'About',
    Page = 'Page',
    End = 'End'
  }

  const { showToast } = useToast();
  const { triggerRefresh } = useRefresh();

  const setLoadMessage = (message: string) => {
    showToast(message, 'success');
  };

  useEffect(() => {
    if (errors.length > 0) {
      showToast('There was a problem', 'error');
    }
  }, [errors]);

  const [showPageType, setShowPageType] = useState(ShowPageTypeEnum.Welcome);

  const [isEditing, setIsEditing] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [addErrors, setAddErrors] = useState([] as string[]);
  const [deleteLoading, setDeleteLoading] = useState([] as string[]);
  const [deleteSessionLoading, setDeleteSessionLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string>('');

  const [showings, setShowings] = useState<ShowingDetails[]>([]);

  const [deferMessageClearCount, setDeferMessageClearCount] = useState(0);

  const [isChangingIndex, setIsChangingIndex] = useState(false);
  const [showSharing, setShowSharing] = useState(false);
  const [showStyle, setShowStyle] = useState(false);

  const { reference } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasUnsavedChanges, setHasUnsavedChanges } = useUnsavedChanges();

  if (!reference) {
    return <NotFound />;
  }

  useEffect(() => {
    if (deferMessageClearCount === 0) {
      setLoadMessage('');
    }
  }, [deferMessageClearCount]);

  useEffect(() => {
    if (loading === undefined || loading === true) {
      return;
    }
    const pageParam = searchParams.get('page');

    if (pageParam) {
      if (
        pageParam === 'Welcome' ||
        (hasFeature('WelcomeCustomPage') && pageParam === 'About') ||
        pageParam === 'End'
      ) {
        setShowPageType(pageParam as ShowPageTypeEnum);
        setSelectedPage(null);
        smoothScrollTo(`placeholder-${pageParam.toLowerCase()}`);
      } else {
        const foundPage = sessionPages.find((p) => p.reference === pageParam);
        if (foundPage) {
          setSelectedPage(foundPage);
          setShowPageType(ShowPageTypeEnum.Page);
          smoothScrollTo(`slide-${foundPage.reference}`);
        } else {
          setShowPageType(ShowPageTypeEnum.Welcome);
          setSelectedPage(null);
          smoothScrollTo('placeholder-welcome');
        }
      }
    } else {
      // default to Welcome if no param
      setShowPageType(ShowPageTypeEnum.Welcome);
      setSelectedPage(null);
      smoothScrollTo('placeholder-welcome');
    }
  }, [searchParams, sessionPages]);

  useEffect(() => {
    if (loading === undefined || loading === true) {
      return;
    }
    if (
      showPageType !== ShowPageTypeEnum.Page &&
      searchParams.get('page') !== showPageType
    ) {
      handlePageChange(showPageType);
    }
  }, [showPageType]);

  const navigate = useNavigate();

  const getSession = async () => {
    if (!!loading || !reference) {
      return;
    }
    setLoading(true);
    try {
      const [sessionResponse, sessionPagesResponse, showingsResponse] =
        await Promise.all([
          get(reference),
          listSessionPages(reference),
          listShowings(reference)
        ]);
      if (
        sessionResponse.success &&
        sessionPagesResponse.success &&
        showingsResponse.success
      ) {
        setSession(sessionResponse.data);
        setSessionPages(sessionPagesResponse.data?.pages || []);
        setShowings(showingsResponse.data?.showings || []);
        setErrors([]);
      } else {
        setLoadMessage('');
        setErrors([
          ...sessionResponse.errors,
          ...sessionPagesResponse.errors,
          ...showingsResponse.errors
        ]);
      }
    } catch (error) {
      setErrors(['There was a problem loading the event']);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (request: AddSessionRequest) => {
    setAddLoading(true);

    try {
      const result = await update(reference, request);
      if (result.success && result.data) {
        setLoadMessage('Updated');
        setIsEditing(false);
        await getSession();
        return true;
      } else {
        setAddErrors(result.errors);
        return false;
      }
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async (sessionPage: SessionPageDetails) => {
    if (deleteLoading.includes(sessionPage.reference)) {
      return;
    }
    setDeleteLoading([...deleteLoading, sessionPage.reference]);

    const result = await removePage(reference, sessionPage.reference);

    if (result.success) {
      bypassPromptRef.current = true;
      setHasUnsavedChanges(false);
      const pageIndex = sessionPages.indexOf(sessionPage);
      if (sessionPages.length === 1) {
        setShowPageType(ShowPageTypeEnum.Welcome);
        setSelectedPage(null);
      } else {
        let nextIndex = pageIndex + 1;
        if (sessionPages.length === nextIndex) {
          nextIndex = nextIndex - 2;
        }
        setSelectedPage(sessionPages[nextIndex]);
      }
      setSessionPages((p) => p.filter((q) => q !== sessionPage));
      setLoadMessage('Slide deleted');
      setDeferMessageClearCount(1);
    }

    setDeleteLoading(deleteLoading.filter((p) => p !== sessionPage.reference));
  };

  useEffect(() => {
    getSession();
    setHasUnsavedChanges(false);
  }, []);

  const [pendingPageChange, setPendingPageChange] = useState<string | null>(
    null
  );
  const bypassPromptRef = useRef(false);
  useEffect(() => {
    if (!hasUnsavedChanges && pendingPageChange !== null) {
      setSearchParams({ page: pendingPageChange });
      setPendingPageChange(null);
    }
  }, [hasUnsavedChanges, pendingPageChange]);

  const showPage = (sessionPageDetails: SessionPageDetails) => {
    const newReference =
      sessionPageDetails?.reference ?? ShowPageTypeEnum.Welcome;

    const isSamePage =
      showPageType === ShowPageTypeEnum.Page &&
      selectedPage?.reference === sessionPageDetails.reference;

    if (hasUnsavedChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes, do you want to discard them?'
      );
      if (!confirmed) {
        return;
      }

      bypassPromptRef.current = true;
      setHasUnsavedChanges(false);
    }

    if (deferMessageClearCount > 0) {
      setDeferMessageClearCount((p) => p - 1);
    }

    if (isSamePage) {
      setSelectedPage(null);
      setShowPageType(ShowPageTypeEnum.Welcome);
      handlePageChange('Welcome');
    } else {
      setSelectedPage(sessionPageDetails);
      setShowPageType(ShowPageTypeEnum.Page);
      handlePageChange(newReference);
    }
  };

  usePrompt(
    'You have unsaved changes, do you want to discard them?',
    hasUnsavedChanges,
    bypassPromptRef
  );
  const handlePageChange = (newPageParam: string) => {
    setSearchParams({ page: newPageParam });
  };
  const startPreview = async () => {
    if (!reference) {
      return;
    }
    const preview = await createPreview(reference);

    if (!preview.success || !preview.data) {
      setErrors(preview.errors);
      return;
    }
    navigate(`/Sessions/${reference}/Present/${preview.data.reference}`);
  };

  const onDragEnd = async (result: DropResult) => {
    const { destination, source } = result;
    // If there's no destination (outside the list), or if the item is dropped back in the same position, do nothing.
    if (!destination || destination.index === source.index || isChangingIndex) {
      return;
    }

    setIsChangingIndex(true);

    try {
      // Reorder the list
      const newPages = Array.from(sessionPages);
      const [movedPage] = newPages.splice(source.index, 1);
      newPages.splice(destination.index, 0, movedPage);

      setSessionPages(newPages);

      const updatedOrder = newPages.map((page, index) => ({
        reference: page.reference,
        orderIndex: index
      }));

      const response = await updatePages(reference, updatedOrder);

      if (response.success && response.data?.pages) {
        setSessionPages(response.data?.pages);
      } else {
        setErrors(response.errors);
      }
    } catch {
      setErrors(['There was a problem updating the order']);
    } finally {
      setIsChangingIndex(false);
    }
  };

  const [selectedPageData, setSelectedPageData] = useState<
    SessionPageDetails | undefined
  >();

  useEffect(() => {
    if (selectedPage) {
      const selected = sessionPages.find(
        (p) => p.reference === selectedPage.reference
      );
      setSelectedPageData(selected);
      setSelectedValue(`page:${selectedPage.reference}`);
    } else {
      setSelectedPageData(undefined);
      setSelectedValue('placeholder:Welcome');
    }
  }, [selectedPage]);

  const addNewItem = async () => {
    const page = await addPage({});

    if (page.success && page.data) {
      const newPage = await addSessionPage(reference, {
        name: `New Slide ${sessionPages.length + 1}`,
        pageReference: page.data.reference
      });

      if (newPage.success && newPage.data) {
        const pageData = newPage.data;
        setSessionPages((p) => [...p, pageData]);
        setSelectedPage(pageData);
        setShowPageType(ShowPageTypeEnum.Page);
        handlePageChange(pageData.reference);
        setLoadMessage('');
        setErrors([]);
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }
    }
  };

  const mainBody = () => {
    if (
      showPageType === ShowPageTypeEnum.Page &&
      selectedPageData &&
      selectedPageData.pageReference
    ) {
      return (
        <PageEditor
          pageReference={selectedPageData.pageReference}
          setMessage={(p) => setLoadMessage(p)}
          setErrors={(p) => setErrors(p)}
        />
      );
    }

    if (showPageType === ShowPageTypeEnum.Welcome) {
      return (
        <SessionWelcomeEditor
          sessionReference={reference}
          setMessage={(p) => setLoadMessage(p)}
          setErrors={(p) => setErrors(p)}
        />
      );
    }

    if (showPageType === ShowPageTypeEnum.End) {
      return (
        <SessionEndEditor
          sessionReference={reference}
          setMessage={(p) => setLoadMessage(p)}
          setErrors={(p) => setErrors(p)}
        />
      );
    }

    if (
      hasFeature('WelcomeCustomPage') &&
      showPageType === ShowPageTypeEnum.About
    ) {
      return (
        <SessionWelcomePageEditor
          sessionReference={reference}
          setMessage={(p) => setLoadMessage(p)}
          setErrors={(p) => setErrors(p)}
        />
      );
    }

    return undefined;
  };

  const saveName = async (value: string) => {
    if (!selectedPage || !selectedPageData || selectedPageData.name === value) {
      return;
    }

    const result = await updateSessionPage(
      reference,
      selectedPageData.reference,
      {
        name: value
      }
    );

    if (result.success && result.data) {
      setSessionPages((p) =>
        p.map((q) => {
          if (q.reference === result.data?.reference) {
            return result.data;
          }
          return q;
        })
      );
    }
  };

  const handleSessionDelete = async () => {
    setDeleteSessionLoading(true);

    const result = await remove(reference);

    if (result.success) {
      navigate('/sessions?deletedSession=true');
    } else {
      setDeleteSessionLoading(false);
      setErrors(result.errors);
    }
  };

  const handlePresent = () => {
    const futureShowings = showings.filter(
      (p) =>
        !p.isPreview && (!p.endDateUtc || new Date(p.endDateUtc) > new Date())
    );

    if (futureShowings.length === 0 && session?.defaultShowing) {
      navigate(
        `/Sessions/${reference}/Present/${session.defaultShowing.reference}`
      );
      return;
    }

    if (futureShowings.length === 1) {
      navigate(`/Sessions/${reference}/Present/${futureShowings[0].reference}`);
      return;
    }

    navigate(`/Sessions/${reference}/Showings`);
    return;
  };

  // Copied from PresentShowing, only difference is .taskbar-contents
  function smoothScrollTo(targetId: string, duration = 500): void {
    const target = document.getElementById(targetId);
    if (!target) return;

    const container = document.querySelector<HTMLElement>('.taskbar-contents');
    if (!container) return;

    const containerHeight = container.clientHeight;
    const targetPosition =
      target.offsetTop -
      container.offsetTop -
      containerHeight / 2 +
      target.clientHeight / 2;
    const startPosition = container.scrollTop;
    const distance = targetPosition - startPosition;
    let startTime: number | null = null;

    function animationStep(currentTime: number): void {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      const ease =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

      if (container) {
        container.scrollTop = startPosition + distance * ease;
      } else {
        return;
      }

      if (timeElapsed < duration) {
        requestAnimationFrame(animationStep);
      }
    }

    requestAnimationFrame(animationStep);
  }

  const scrollRef = useRef<HTMLDivElement>(null);
  const prevLengthRef = useRef(0);

  useEffect(() => {
    if (
      sessionPages.length > prevLengthRef.current &&
      scrollRef.current &&
      showPageType === ShowPageTypeEnum.Page
    ) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevLengthRef.current = sessionPages.length;
  }, [sessionPages]);

  if (loading === false && !session) {
    return (
      <Feedback message="There was a problem loading the event" type="error" />
    );
  }

  return (
    <>
      {loading && <LoadingSpinner />}

      {loading === false && (
        <>
          <div className="edit-area">
            <div className="taskbar-full">
              <div className="taskbar-contents" ref={scrollRef}>
                <BackButton target="/dashboard" text={'View all events'} />
                <h2>{session?.name}</h2>
                <h3>Editing</h3>
                <h4>Placeholder screens</h4>
                <div className="placeholder-items">
                  <Button
                    id={'placeholder-welcome'}
                    className={`placeholder-item draggable ${showPageType === ShowPageTypeEnum.Welcome ? 'active' : ''}`}
                    onClick={() => setShowPageType(ShowPageTypeEnum.Welcome)}
                  >
                    Welcome screen
                  </Button>
                  <FeatureGate feature="WelcomeCustomPage">
                    <div className="inset">
                      <Reply />
                      <Button
                        id={'placeholder-about'}
                        className={`placeholder-item draggable ${showPageType === ShowPageTypeEnum.About ? 'active' : ''}`}
                        onClick={() => setShowPageType(ShowPageTypeEnum.About)}
                      >
                        About screen
                      </Button>
                    </div>
                  </FeatureGate>
                  <Button
                    id={'placeholder-end'}
                    className={`placeholder-item draggable ${showPageType === ShowPageTypeEnum.End ? 'active' : ''}`}
                    onClick={() => setShowPageType(ShowPageTypeEnum.End)}
                  >
                    End screen
                  </Button>
                </div>
                <h4>Programme items</h4>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable
                    droppableId="pages"
                    isDropDisabled={isChangingIndex}
                  >
                    {(provided) => (
                      <div
                        className="sections"
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                      >
                        {sessionPages.map((p, index) => (
                          <Draggable
                            key={p.reference}
                            draggableId={p.reference}
                            index={index}
                          >
                            {(provided) => (
                              <div
                                id={`slide-${p.reference}`}
                                key={p.reference}
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`section draggable ${showPageType === ShowPageTypeEnum.Page && selectedPage?.reference === p.reference ? 'active' : ''}`}
                                onClick={() => showPage(p)}
                              >
                                <FaGripVertical />
                                <span>{p.name}</span>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <Button onClick={addNewItem} type="Secondary">
                <FaPlus />
                <span>Add slide</span>
              </Button>
            </div>
            <div className="content-area">
              <div className="content-header">
                <div className="details">
                  <h2>{session?.name}</h2>
                </div>
                <div className="actions with-buttons">
                  <Button
                    type="Outline"
                    onClick={() => setShowSharing(true)}
                    size="Small"
                  >
                    Share
                  </Button>
                  <FeatureGate feature="MultipleShowings">
                    <Button
                      type="Outline"
                      onClick={() =>
                        navigate(`/Sessions/${reference}/Showings`)
                      }
                      size="Small"
                    >
                      Showings
                    </Button>
                  </FeatureGate>
                  <Button
                    type="Outline"
                    onClick={() => startPreview()}
                    size="Small"
                  >
                    Preview
                  </Button>
                  <Button onClick={() => handlePresent()} size="Small">
                    Present <PresentIcon />
                  </Button>
                  <GenericSettingsMenu
                    title="Event settings"
                    actions={[
                      {
                        text: 'Edit event details',
                        children: (
                          <>
                            <EditIcon /> Edit event details
                          </>
                        ),
                        action: () => setIsEditing(true)
                      },
                      {
                        text: 'Edit theme',
                        children: (
                          <>
                            <ColoursIcon /> Edit theme
                          </>
                        ),
                        action: () => setShowStyle(true)
                      }
                    ]}
                    secondaryActions={[
                      {
                        text: 'Delete',
                        children: (
                          <>
                            <DeleteIcon /> Delete
                          </>
                        ),
                        action: () => handleSessionDelete(),
                        deleteMessage:
                          'Are you sure you want to delete this event?',
                        isLoading: deleteSessionLoading
                      }
                    ]}
                    responsiveActions={[
                      {
                        text: 'Share',
                        action: () => setShowSharing(true)
                      },
                      {
                        text: 'Showings',
                        action: () =>
                          navigate(`/Sessions/${reference}/Showings`)
                      },
                      {
                        text: 'Preview',
                        action: () => startPreview()
                      },
                      {
                        text: 'Present',
                        children: (
                          <>
                            <PresentIcon /> Present
                          </>
                        ),
                        action: () => handlePresent()
                      }
                    ]}
                  />
                </div>
              </div>
              <div className="inline-page-navigation">
                <form className="content-form content-form-inline">
                  <select
                    value={selectedValue}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSelectedValue(value);
                      if (value.startsWith('placeholder:')) {
                        const type = value.split(
                          ':'
                        )[1] as keyof typeof ShowPageTypeEnum;
                        setShowPageType(ShowPageTypeEnum[type]);
                        handlePageChange(type);
                      } else if (value.startsWith('page:')) {
                        const ref = value.split(':')[1];
                        const page = sessionPages.find(
                          (p) => p.reference === ref
                        );
                        if (page) {
                          showPage(page);
                        }
                      }
                    }}
                  >
                    <optgroup label="Placeholder screens">
                      <option value="placeholder:Welcome">
                        Welcome screen
                      </option>
                      <FeatureGate feature="WelcomeCustomPage">
                        <option value="placeholder:About">About screen</option>
                      </FeatureGate>
                      <option value="placeholder:End">End screen</option>
                    </optgroup>
                    <optgroup label="Programme items">
                      {sessionPages.map((p) => (
                        <option key={p.reference} value={`page:${p.reference}`}>
                          {p.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <Button onClick={addNewItem} type="Secondary">
                    <FaPlus />
                  </Button>
                  {showPageType === ShowPageTypeEnum.Page &&
                  selectedPageData ? (
                    <DeleteButton
                      type="Secondary"
                      size="Small"
                      isLoading={deleteLoading.includes(
                        selectedPageData.reference
                      )}
                      onClick={() => handleDelete(selectedPageData)}
                      message="Are you sure you want to delete this slide?"
                      aria-label="Delete slide"
                      title="Delete slide"
                    >
                      <DeleteIcon />
                    </DeleteButton>
                  ) : (
                    <DeleteButton
                      type="Secondary"
                      size="Small"
                      disabled={true}
                      isLoading={false}
                      onClick={() => {
                        /* Do nothing */
                      }}
                      message="Are you sure you want to delete this slide?"
                    >
                      <DeleteIcon />
                    </DeleteButton>
                  )}
                </form>
              </div>
              <div className="content-body">
                {showPageType === ShowPageTypeEnum.Page && selectedPageData && (
                  <form className="content-form content-form-inline">
                    {/* <AutoSaveInput
                      id={`editSlideName-${selectedPageData.reference}`}
                      placeholder="Slide name"
                      required={true}
                      initialValue={selectedPageData.name}
                      save={saveName}
                    /> */}
                    <EditableLabel
                      label={selectedPageData.name ?? ''}
                      itemId={selectedPageData.reference}
                      onSave={(p) => saveName(p)}
                    />
                    <div className="content-form__full-page-only">
                      <DeleteButton
                        type="Secondary"
                        size="Small"
                        isLoading={deleteLoading.includes(
                          selectedPageData.reference
                        )}
                        onClick={() => handleDelete(selectedPageData)}
                        message="Are you sure you want to delete this slide?"
                        aria-label="Delete slide"
                        title="Delete slide"
                      >
                        <DeleteIcon />
                      </DeleteButton>
                    </div>
                  </form>
                )}
                <Feedback messages={errors} type="error" /> {mainBody()}
              </div>
            </div>
          </div>
        </>
      )}

      {isEditing && (
        <ModalForm>
          <SessionDetailsForm
            errors={addErrors}
            isSubmitting={addLoading}
            model={session}
            submit={handleEdit}
            close={() => {
              setIsEditing(false);
              setAddErrors([]);
            }}
            buttonText="Update"
          />
        </ModalForm>
      )}

      {showSharing && session && (
        <SessionSharingDetails
          close={() => setShowSharing(false)}
          session={session}
        />
      )}

      {showStyle && session?.reference && (
        <SessionStyleEditor
          close={(isSuccessful) => {
            setShowStyle(false);
            if (isSuccessful) {
              triggerRefresh();
              setLoadMessage('Theme updated');
            }
          }}
          sessionReference={session?.reference}
        />
      )}
    </>
  );
};

export default Event;
